import { Fragment } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StageRequiredField } from "../stageRequirements";

/** Props for the StageReadiness component. */
interface StageReadinessProps {
  stageName: string;
  fields: StageRequiredField[];
  onSelectField: (field: StageRequiredField) => void;
}

/**
 * Summarizes how many of a stage's required fields are filled, naming the ones
 * still needed as buttons that jump to each field.
 * @param stageName - The selected stage's name
 * @param fields - The stage's required fields with their filled state
 * @param onSelectField - Called when a still-needed field name is clicked
 * @returns The readiness summary
 */
export function StageReadiness({
  stageName,
  fields,
  onSelectField,
}: StageReadinessProps) {
  const missingFields = fields.filter((field) => !field.isFilled);
  const filledCount = fields.length - missingFields.length;
  const isReady = fields.length > 0 && missingFields.length === 0;

  return (
    <div
      aria-live="polite"
      className={cn(
        "mt-2 flex items-start gap-2.5 rounded-md border bg-row-stripe px-3 py-2.5 text-sm",
        "transition-colors duration-300 motion-reduce:transition-none",
        isReady && "border-success/40 bg-success/[0.07]",
      )}
    >
      {fields.length > 0 && (
        <span className="flex shrink-0 gap-1 pt-1.5" aria-hidden="true">
          {fields.map((field, index) => (
            <span
              key={`${field.kind}:${field.label}`}
              className={cn(
                "h-2 w-2 rounded-full bg-border transition-colors duration-200 motion-reduce:transition-none",
                index < filledCount && "bg-foreground",
                isReady && "bg-success",
              )}
            />
          ))}
        </span>
      )}
      <p className="min-w-0 [overflow-wrap:anywhere]">
        {fields.length === 0 && (
          <span className="text-muted-foreground">
            {stageName} has no required fields.
          </span>
        )}
        {isReady && (
          <>
            <Check className="mr-1 inline h-3.5 w-3.5 -translate-y-px text-success" />
            Everything {stageName} needs is filled.
          </>
        )}
        {missingFields.length > 0 && (
          <>
            <span className="font-semibold tabular-nums">
              {filledCount} of {fields.length}
            </span>{" "}
            filled for {stageName}.{" "}
            <span className="text-muted-foreground">
              Still needed:{" "}
              {missingFields.map((field, index) => (
                <Fragment key={`${field.kind}:${field.label}`}>
                  {index > 0 && ", "}
                  <button
                    type="button"
                    onClick={() => onSelectField(field)}
                    className="rounded-sm text-foreground underline underline-offset-2 hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {field.label}
                  </button>
                </Fragment>
              ))}
            </span>
          </>
        )}
      </p>
    </div>
  );
}
